import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrencyPDF } from "./formatters";
import { toast } from "sonner";

const C = {
    primary: [30, 58, 138],
    accent: [99, 102, 241],
    gray: [100, 100, 100],
    lightBg: [245, 247, 255],
    red: [220, 38, 38],
    amber: [217, 119, 6],
    green: [22, 163, 74],
    white: [255, 255, 255],
};

const tableStyles = {
    theme: 'striped',
    headStyles: {
        fillColor: C.primary,
        textColor: C.white,
        fontStyle: 'bold',
        fontSize: 9,
    },
    bodyStyles: {
        fontSize: 9,
        textColor: [40, 40, 40],
    },
    alternateRowStyles: { fillColor: [248, 250, 255] },
    margin: { left: 14, right: 14 },
};

const addHeader = (doc, pw) => {
    doc.setFillColor(...C.primary);
    doc.rect(0, 0, pw, 40, 'F');

    doc.setTextColor(...C.white);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Prowen Hotel Analytics', 14, 18);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Competitive Intelligence Report', 14, 30);

    doc.setTextColor(200, 200, 200);
    doc.setFontSize(9);
    doc.text(`Generated: ${format(new Date(), 'PPP')}`, pw - 14, 20, { align: 'right' });
};

const addFooter = (doc, pw, ph, currentPage, totalPages) => {
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
        `© ${new Date().getFullYear()} Prowen Technologies India Private Limited  |  Page ${currentPage} of ${totalPages}`,
        pw / 2,
        ph - 10,
        { align: 'center' }
    );
};

const addSectionTitle = (doc, title, y) => {
    doc.setTextColor(...C.primary);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, y);
    doc.setDrawColor(...C.primary);
    doc.setLineWidth(0.4);
    doc.line(14, y + 2, doc.internal.pageSize.getWidth() - 14, y + 2);
    return y + 10;
};

/** Add a new page and reset y if content won't fit */
const checkNewPage = (doc, y, neededHeight = 40) => {
    const ph = doc.internal.pageSize.getHeight();
    if (y + neededHeight > ph - 20) {
        doc.addPage();
        return 20;
    }
    return y;
};

export const generateBasicPDF = async (data, currency) => {

    // const fontBase64 = await loadFontAsBase64("/fonts/NotoSans-Regular.ttf");

    // use font
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();

    addHeader(doc, pw);
    let y = 55;

    // ── Period badge ──
    doc.setFillColor(...C.lightBg);
    doc.roundedRect(14, y - 4, pw - 28, 14, 3, 3, 'F');
    doc.setTextColor(...C.gray);
    // doc.addFileToVFS("NotoSans-Regular.ttf", fontBase64);
    // doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
    // doc.setFont("NotoSans", "normal");
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
        `Analysis Period: ${data?.dateRange?.start ?? '-'} to ${data?.dateRange?.end ?? '-'}  |  ${data?.daysAnalyzed ?? 0} days analyzed  |  Basic Report`,
        20, y + 4
    );
    y += 22;

    // ── Executive Summary ──
    y = addSectionTitle(doc, 'Executive Summary', y);
    autoTable(doc, {
        startY: y,
        head: [['Metric', 'Your Hotel', 'Market Avg', 'Difference']],
        body: [
            [
                'Average Rate',
                formatCurrencyPDF(data?.yourHotel?.avgPrice ?? 0, currency),
                formatCurrencyPDF(data?.market?.avgPrice ?? 0, currency),
                `${(data?.yourHotel?.priceVsMarket ?? 0) > 0 ? '+' : ''}${data?.yourHotel?.priceVsMarket ?? 0}%`,
            ],
            [
                'Price Range',
                `${formatCurrencyPDF(data?.yourHotel?.minPrice ?? 0, currency)} – ${formatCurrencyPDF(data?.yourHotel?.maxPrice ?? 0, currency)}`,
                '-', '-',
            ],
            [
                'Market Position',
                `#${data?.market?.position ?? '-'} of ${data?.market?.totalCompetitors ?? '-'}`,
                '-', '-',
            ],
            [
                'Guest Rating',
                `${data?.yourHotel?.rating ?? '-'}/5 (${data?.yourHotel?.reviews ?? 0} reviews)`,
                '-', '-',
            ],
            [
                'Rate Parity undercuts',
                `${data?.rateParity?.totalViolations ?? 0}`,
                '-',
                data?.rateParity?.otaWithMostViolations ?? '-',
            ],
        ],
        ...tableStyles,
    });
    y = doc.lastAutoTable.finalY + 18;

    // ── Rate Parity & OTA ──
    y = checkNewPage(doc, y, 60);
    y = addSectionTitle(doc, 'Rate Parity & OTA Performance', y);
    doc.setTextColor(...C.gray);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
        `Total undercuts detected: ${data?.rateParity?.totalViolations ?? 0}  |  Primary violator: ${data?.rateParity?.otaWithMostViolations ?? '-'}`,
        22, y
    );
    y += 10;

    if ((data?.otaSummary?.length ?? 0) > 0) {
        autoTable(doc, {
            startY: y,
            head: [['OTA Channel', 'Avg Price', 'Min Price', 'Parity Status']],
            body: (data?.otaSummary ?? []).map(ota => [
                ota?.ota ?? '-',
                formatCurrencyPDF(ota?.avgPrice ?? 0, currency),
                formatCurrencyPDF(ota?.minPrice ?? 0, currency),
                (ota?.violations ?? 0) > 0 ? `${ota.violations} undercut(s)` : 'Compliant',
            ]),
            ...tableStyles,
        });
        y = doc.lastAutoTable.finalY + 18;
    }

    // ── Competitive Analysis ──
    y = checkNewPage(doc, y, 60);
    y = addSectionTitle(doc, 'Competitive Analysis', y);

    if ((data?.competitorSummary?.length ?? 0) > 0) {
        const yourAvg = Number(data?.yourHotel?.avgPrice ?? 0);
        autoTable(doc, {
            startY: y,
            head: [['Competitor', 'Avg Price', 'Min Price', 'Max Price', 'vs You']],
            body: (data?.competitorSummary ?? []).map(comp => {
                const diff = yourAvg > 0
                    ? Math.round(((Number(comp?.avgPrice ?? 0) - yourAvg) / yourAvg) * 100)
                    : 0;
                return [
                    comp?.name?.length > 50 ? comp?.name?.slice(0, 50) + "..." : comp?.name ?? '-',
                    formatCurrencyPDF(comp?.avgPrice ?? 0, currency),
                    formatCurrencyPDF(comp?.minPrice ?? 0, currency),
                    formatCurrencyPDF(comp?.maxPrice ?? 0, currency),
                    `${diff > 0 ? '+' : ''}${diff}%`,
                ];
            }),
            ...tableStyles,
        });
        y = doc.lastAutoTable.finalY + 18;
    }

    // ── Amenities Comparison ──
    y = checkNewPage(doc, y, 80);
    y = addSectionTitle(doc, 'Amenities Comparison', y);

    if ((data?.amenitySummary?.length ?? 0) > 0) {
        // Derive score keys dynamically from the first entry
        const scoreKeys = Object.keys(data.amenitySummary[0]?.scores ?? {});
        autoTable(doc, {
            startY: y,
            head: [['Hotel', ...scoreKeys, 'Avg']],
            body: (data?.amenitySummary ?? []).map(a => [
                a?.hotel ?? '-',
                ...scoreKeys.map(k => (a?.scores?.[k] ?? '-').toString()),
                a?.avgScore ?? '-',
            ]),
            ...tableStyles,
            headStyles: { ...tableStyles.headStyles, fontSize: 8 },
            bodyStyles: { ...tableStyles.bodyStyles, fontSize: 8 },
            columnStyles: { 0: { cellWidth: 40 } },
        });
        y = doc.lastAutoTable.finalY + 18;
    }

    // ── Demand & Events ──
    y = checkNewPage(doc, y, 60);
    y = addSectionTitle(doc, 'Demand & Events', y);

    autoTable(doc, {
        startY: y,
        head: [['Date', 'Event', 'Demand Impact', 'Pricing Recommendation']],
        body: (data?.eventsSummary ?? []).map(e => [
            e?.date ?? '-',
            e?.event ?? '-',
            e?.impact ?? '-',
            e?.recommendation ?? '-',
        ]),
        ...tableStyles,
    });

    // ── Footers ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(doc, pw, ph, i, totalPages);
    }

    doc.save(`Prowen_Basic_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('Basic Report downloaded!');
};

// ─────────────────────────────────────────────
//  generateAIPDF
// ─────────────────────────────────────────────

export const generateAIPDF = (data, currency) => {
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();

    const aiTableStyles = {
        ...tableStyles,
        headStyles: { ...tableStyles.headStyles, fillColor: C.accent },
    };

    addHeader(doc, pw);
    let y = 55;

    // ── Period badge (AI) ──
    doc.setFillColor(99, 102, 241, 15);
    doc.roundedRect(14, y - 4, pw - 28, 14, 3, 3, 'F');
    doc.setTextColor(...C.accent);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(
        `Analysis Period: ${data?.dateRange?.start ?? '-'} to ${data?.dateRange?.end ?? '-'}  |  ${data?.daysAnalyzed ?? 0} days  |  AI-Powered Report`,
        20, y + 4
    );
    y += 22;

    // ── Executive Summary ──
    y = addSectionTitle(doc, 'Executive Summary', y);
    autoTable(doc, {
        startY: y,
        head: [['Metric', 'Your Hotel', 'Market Avg', 'Difference']],
        body: [
            [
                'Average Rate',
                formatCurrencyPDF(data?.yourHotel?.avgPrice ?? 0, currency),
                formatCurrencyPDF(data?.market?.avgPrice ?? 0, currency),
                `${(data?.yourHotel?.priceVsMarket ?? 0) > 0 ? '+' : ''}${data?.yourHotel?.priceVsMarket ?? 0}%`,
            ],
            [
                'Price Range',
                `${formatCurrencyPDF(data?.yourHotel?.minPrice ?? 0, currency)} – ${formatCurrencyPDF(data?.yourHotel?.maxPrice ?? 0, currency)}`,
                '-', '-',
            ],
            [
                'Market Position',
                `#${data?.market?.position ?? '-'} of ${data?.market?.totalCompetitors ?? '-'}`,
                '-', '-',
            ],
            [
                'Guest Rating',
                `${data?.yourHotel?.rating ?? '-'}/5 (${data?.yourHotel?.reviews ?? 0} reviews)`,
                '-', '-',
            ],
            [
                'Rate Parity undercuts',
                `${data?.rateParity?.totalViolations ?? 0}`,
                '-',
                data?.rateParity?.otaWithMostViolations ?? '-',
            ],
        ],
        ...aiTableStyles,
    });
    y = doc.lastAutoTable.finalY + 18;

    // ── AI Insights ──
    if ((data?.insights?.length ?? 0) > 0) {
        y = checkNewPage(doc, y, 60);
        y = addSectionTitle(doc, 'AI-Powered Insights & Recommendations', y);

        const typeColors = {
            critical: C.red,
            warning: C.amber,
            success: C.green,
            info: C.accent,
        };

        (data?.insights ?? []).forEach((insight, idx) => {
            y = checkNewPage(doc, y, 30);

            const color = typeColors[insight?.type] ?? C.gray;
            doc.setFillColor(...color);
            doc.roundedRect(14, y - 2, 3, 16, 1, 1, 'F');

            doc.setTextColor(40, 40, 40);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`${idx + 1}. ${insight?.title ?? 'Insight'}`, 22, y + 4);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(...C.gray);
            const lines = doc.splitTextToSize(insight?.description ?? '', pw - 40);
            doc.text(lines, 22, y + 11);
            y += 14 + lines.length * 4.5 + 6;
        });
        y += 4;
    }

    // ── Rate Parity & OTA ──
    y = checkNewPage(doc, y, 60);
    y = addSectionTitle(doc, 'Rate Parity & OTA Performance', y);
    doc.setTextColor(...C.gray);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
        `Total undercuts detected: ${data?.rateParity?.totalViolations ?? 0}  |  Primary violator: ${data?.rateParity?.otaWithMostViolations ?? '-'}`,
        22, y
    );
    y += 10;

    if ((data?.otaSummary?.length ?? 0) > 0) {
        autoTable(doc, {
            startY: y,
            head: [['OTA Channel', 'Avg Price', 'Min Price', 'Parity Status']],
            body: (data?.otaSummary ?? []).map(ota => [
                ota?.ota ?? '-',
                formatCurrencyPDF(ota?.avgPrice ?? 0, currency),
                formatCurrencyPDF(ota?.minPrice ?? 0, currency),
                (ota?.violations ?? 0) > 0 ? `${ota.violations} undercut(s)` : 'Compliant',
            ]),
            ...aiTableStyles,
        });
        y = doc.lastAutoTable.finalY + 18;
    }

    // ── Competitive Analysis ──
    y = checkNewPage(doc, y, 60);
    y = addSectionTitle(doc, 'Competitive Analysis', y);

    if ((data?.competitorSummary?.length ?? 0) > 0) {
        const yourAvg = Number(data?.yourHotel?.avgPrice ?? 0);
        autoTable(doc, {
            startY: y,
            head: [['Competitor', 'Avg Price', 'Min Price', 'Max Price', 'vs You']],
            body: (data?.competitorSummary ?? []).map(comp => {
                const diff = yourAvg > 0
                    ? Math.round(((Number(comp?.avgPrice ?? 0) - yourAvg) / yourAvg) * 100)
                    : 0;
                return [
                    comp?.name?.length > 50 ? comp?.name?.slice(0, 50) + "..." : comp?.name ?? '-',
                    formatCurrencyPDF(comp?.avgPrice ?? 0, currency),
                    formatCurrencyPDF(comp?.minPrice ?? 0, currency),
                    formatCurrencyPDF(comp?.maxPrice ?? 0, currency),
                    `${diff > 0 ? '+' : ''}${diff}%`,
                ];
            }),
            ...aiTableStyles,
        });
        y = doc.lastAutoTable.finalY + 18;
    }

    // ── Amenities Comparison ──
    y = checkNewPage(doc, y, 80);
    y = addSectionTitle(doc, 'Amenities Comparison', y);

    if ((data?.amenitySummary?.length ?? 0) > 0) {
        const scoreKeys = Object.keys(data.amenitySummary[0]?.scores ?? {});
        autoTable(doc, {
            startY: y,
            head: [['Hotel', ...scoreKeys, 'Avg']],
            body: (data?.amenitySummary ?? []).map(a => [
                a?.hotel ?? '-',
                ...scoreKeys.map(k => (a?.scores?.[k] ?? '-').toString()),
                a?.avgScore ?? '-',
            ]),
            ...aiTableStyles,
            headStyles: { ...aiTableStyles.headStyles, fontSize: 8 },
            bodyStyles: { ...aiTableStyles.bodyStyles, fontSize: 8 },
            columnStyles: { 0: { cellWidth: 40 } },
        });
        y = doc.lastAutoTable.finalY + 18;
    }

    // ── Demand & Events ──
    y = checkNewPage(doc, y, 60);
    y = addSectionTitle(doc, 'Demand & Events', y);

    autoTable(doc, {
        startY: y,
        head: [['Date', 'Event', 'Demand Impact', 'AI Pricing Recommendation']],
        body: (data?.eventsSummary ?? []).map(e => [
            e?.date ?? '-',
            e?.event ?? '-',
            e?.impact ?? '-',
            e?.recommendation ?? '-',
        ]),
        ...aiTableStyles,
    });

    // ── Footers ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(doc, pw, ph, i, totalPages);
    }

    doc.save(`Prowen_AI_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('AI-Powered Report downloaded!');
};